import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtNumber } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Truck, AlertTriangle, Clock, Settings, Gauge, Download, Pencil, Check, X, RefreshCw, ChevronDown, BookOpen } from "lucide-react";
import { FleetGuideSheet } from "../components/fleet/FleetGuideSheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ServiceSettingsDialog } from "@/components/fleet/ServiceSettingsDialog";
import { VehicleScorecard } from "@/components/fleet/VehicleScorecard";
import { OriginBadge } from "@/components/common/OriginBadge";
import { usePermissions } from "@/context/PermissionsContext";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-200",
};
const STATUS_LABEL = { ACTIVE: "Active", ARCHIVED: "Archived" };

const PIE_COLORS = ["#00334C", "#004a6b", "#fcea00", "#94a3b8"];

const TID = {
  vehicleRow: (rego) => `vehicle-row-${rego}`,
  vehicleSearch: "vehicle-search-input",
  vehicleStatusFilter: "vehicle-status-filter",
  vehicleTypeFilter: "vehicle-type-filter",
};

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("fleet");
  const [svcFilter, setSvcFilter] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [matchSummary, setMatchSummary] = useState(null);
  const [editRego, setEditRego] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [costSyncing, setCostSyncing] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const { canSee, can, loaded: permsLoaded } = usePermissions();
  const blocked = permsLoaded && !canSee("vehicles");

  useEffect(() => {
    if (!permsLoaded || blocked) return;
    Promise.all([
      api.get("/vehicles").then((r) => setVehicles(r.data)),
      api.get("/vehicles/stats/summary").then((r) => setSummary(r.data)),
    ]).finally(() => setLoading(false));
    api.get("/fleet/jobs-match-summary").then((r) => setMatchSummary(r.data)).catch(() => {});
    api.get("/fleet/service-cost-backfill/status").then((r) => {
      if (r.data?.running) { setCostSyncing(true); pollCostStatus(); }
    }).catch(() => {});
  }, [permsLoaded, blocked]);

  const saveDriver = async (rego) => {
    try {
      const res = await api.put(`/vehicles/${rego}/driver`, { driver: editVal.trim() });
      toast.success(res.data.cleared ? "Driver cleared — using AroFlo value" : "Driver updated");
      setVehicles((vs) => vs.map((x) => (x.rego === rego ? { ...x, driver: res.data.driver } : x)));
      setEditRego(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update driver");
    }
  };

  const pollCostStatus = () => {
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/fleet/service-cost-backfill/status");
        if (!data.running) {
          clearInterval(poll);
          setCostSyncing(false);
          const r = data.last_result || {};
          if (r.updated != null) toast.success(`Job cost sync finished — ${r.updated} job${r.updated === 1 ? "" : "s"} costed`);
          api.get("/vehicles").then((res) => setVehicles(res.data));
          api.get("/vehicles/stats/summary").then((res) => setSummary(res.data));
        }
      } catch { clearInterval(poll); setCostSyncing(false); }
    }, 5000);
  };

  const syncJobCosts = async () => {
    try {
      setCostSyncing(true);
      const { data } = await api.post("/fleet/service-cost-backfill");
      toast.info(data.status === "already_running"
        ? "A cost sync is already running — it'll finish in the background."
        : "Pulling job costs from AroFlo — this runs in the background and can take a few minutes.");
      pollCostStatus();
    } catch (e) {
      setCostSyncing(false);
      toast.error(e.response?.data?.detail || "Could not start cost sync");
    }
  };

  const downloadCsv = (cols, rows, filename) => {
    const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map((c) => c.label).join(",")];
    rows.forEach((r) => lines.push(cols.map((c) => esc(c.get(r))).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAssetReport = () => {
    if (!vehicles.length) { toast.info("No vehicles to export"); return; }
    const cols = [
      { label: "Rego", get: (v) => v.rego },
      { label: "Make", get: (v) => v.make },
      { label: "Model", get: (v) => v.model },
      { label: "Type", get: (v) => v.type },
      { label: "Year", get: (v) => v.year },
      { label: "Driver", get: (v) => (v.driver && v.driver !== "—" ? v.driver : "") },
      { label: "Base", get: (v) => v.base },
      { label: "Status", get: (v) => v.status },
      { label: "Odometer (km)", get: (v) => v.odometer_km },
      { label: "Odometer Source", get: (v) => v.odometer_source },
      { label: "Odometer As Of", get: (v) => v.odometer_as_of },
      { label: "Last Service", get: (v) => v.last_service_date },
      { label: "Last Service Source", get: (v) => v.last_service_source },
      { label: "Next Due", get: (v) => v.next_due_date },
      { label: "Days To Due", get: (v) => v.days_to_due },
      { label: "Service Status", get: (v) => v.service_status },
    ];
    downloadCsv(cols, vehicles, `vehicle-asset-register-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${vehicles.length} vehicles`);
  };

  const exportReplacementShortlist = async () => {
    try {
      const { data } = await api.get("/vehicles/asset-management/ranking");
      const vs = data.vehicles || [];
      if (!vs.length) { toast.info("No vehicles to rank"); return; }
      const cols = [
        { label: "Rank", get: (v) => v.rank },
        { label: "Rego", get: (v) => v.rego },
        { label: "Vehicle", get: (v) => `${v.make || ""} ${v.model || ""}`.trim() },
        { label: "Type", get: (v) => v.type },
        { label: "Odometer (km)", get: (v) => v.odometer_km },
        { label: "Condition", get: (v) => v.condition },
        { label: "Risk", get: (v) => v.risk },
        { label: "End of life", get: (v) => v.end_of_life },
        { label: "Replacement score", get: (v) => v.replacement_score },
        { label: "Est. maint cost 12mo", get: (v) => v.maint_cost_12mo },
        { label: "Maint trend %", get: (v) => v.maint_trend_pct },
        { label: "EOL suggestion", get: (v) => v.eol_suggestion },
      ];
      downloadCsv(cols, vs, `replacement-shortlist-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(`Exported replacement shortlist (${vs.length} vehicles)`);
    } catch (e) {
      toast.error("Could not load the replacement ranking");
    }
  };

  const emailMissingKmNudge = async () => {
    try {
      const { data } = await api.post("/fleet/missing-km-nudge/send");
      if (data.sent) toast.success(`Missing-km nudge emailed to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
      else toast.info(data.skipped === "no recipients configured"
        ? "Add digest recipients in Service alerts first"
        : `Not sent: ${data.skipped || "nothing to send"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the nudge");
    }
  };

  const exportAmpolZeroKm = async () => {
    try {
      const { data } = await api.get("/fleet/ampol-zero-km");
      if (!data.rows?.length) { toast.info("No Ampol fill-ups are missing a km reading"); return; }
      const cols = [
        { label: "Rego", get: (r) => r.rego },
        { label: "Date", get: (r) => r.date },
        { label: "Month", get: (r) => r.month },
        { label: "KM at pump", get: (r) => r.km },
        { label: "Fuel Type", get: (r) => r.fuel_type },
        { label: "Litres", get: (r) => r.litres },
        { label: "Cost inc GST", get: (r) => r.cost_inc_gst },
        { label: "Site", get: (r) => r.site },
        { label: "Driver", get: (r) => r.driver },
      ];
      downloadCsv(cols, data.rows, `ampol-missing-km-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(`Exported ${data.rows.length} fill-ups missing a km reading across ${data.by_rego.length} vehicles`);
    } catch (e) {
      toast.error("Could not load the Ampol missing-km report");
    }
  };

  const exportUnmatchedJobs = async () => {
    try {
      const { data } = await api.get("/fleet/unmatched-jobs");
      if (!data.jobs?.length) { toast.info("No unmatched jobs — every service job is tied to a vehicle"); return; }
      const cols = ["Filed Under (name on job)", "Job Number", "Date", "Status", "Cost", "Location", "Title"];
      const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
      const lines = [cols.join(",")];
      data.jobs.forEach((j) => {
        lines.push([j.filed_under, j.job_number, j.date, j.status, j.cost, j.location, j.title].map(esc).join(","));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `unmatched-service-jobs-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.jobs.length} unmatched jobs`);
    } catch (e) {
      toast.error("Could not export unmatched jobs");
    }
  };

  const exportServiceDue = () => {
    const rows = vehicles.filter((v) => v.status !== "ARCHIVED" && (v.service_status === "overdue" || v.service_status === "due_soon"));
    if (!rows.length) { toast.info("No overdue or due-soon vehicles to export"); return; }
    const cols = ["Rego", "Make", "Model", "Driver", "Base", "Odometer (km)", "Last Service", "Next Due", "Days To Due", "Service Status"];
    const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    rows.sort((a, b) => (a.days_to_due ?? 1e9) - (b.days_to_due ?? 1e9)).forEach((v) => {
      lines.push([v.rego, v.make, v.model, v.driver, v.base, v.odometer_km, v.last_service_date, v.next_due_date, v.days_to_due, v.service_status].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `service-due-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} vehicles`);
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (status !== "all" && v.status !== status) return false;
      if (type !== "all" && v.type !== type) return false;
      if (svcFilter !== "all" && (v.service_status !== svcFilter || v.status === "ARCHIVED")) return false;
      if (ql && !`${v.rego} ${v.make} ${v.model} ${v.driver} ${v.base}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [vehicles, q, status, type, svcFilter]);
  const { sorted: sortedFleet, sort: fleetSort, setSort: setFleetSort } = useSortable(filtered);

  const types = useMemo(() => Array.from(new Set(vehicles.map((v) => v.type))), [vehicles]);

  const openVehicle = (rego) => setSelected(rego);

  const active = summary?.by_status.find((s) => s.name === "ACTIVE")?.value || 0;

  if (blocked) {
    return (
      <AppLayout title="Vehicle Management" subtitle="GBE Group fleet">
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="vehicles-access-restricted">
          <Truck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-semibold text-slate-800">Fleet management is restricted</div>
          <div className="text-xs text-slate-500 mt-1.5">Your assigned vehicle is shown in HSEQ → My Safety.</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Vehicle Management" subtitle="GBE Group fleet — vehicles, drivers and service schedule">
      {loading ? (
        <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />
      ) : (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              <Kpi label="Fleet Size" value={fmtNumber(summary.total)} icon={Truck} />
              <Kpi label="Active" value={fmtNumber(active)} icon={Truck} tone="emerald" />
              <Kpi label="Service Overdue" value={fmtNumber(summary.service_due?.overdue || 0)} icon={AlertTriangle} tone="rose"
                onClick={() => setSvcFilter(svcFilter === "overdue" ? "all" : "overdue")} selected={svcFilter === "overdue"}
                testId="kpi-service-overdue" />
              <Kpi label={`Due Soon (${summary.service_due?.due_soon_days || 30}d)`} value={fmtNumber(summary.service_due?.due_soon || 0)} icon={Clock} tone="amber"
                onClick={() => setSvcFilter(svcFilter === "due_soon" ? "all" : "due_soon")} selected={svcFilter === "due_soon"}
                testId="kpi-service-due-soon" />
              <Kpi label="Avg Odometer" value={`${fmtNumber(summary.avg_km)} km`} />
              <Kpi label="Fleet Total km" value={`${fmtNumber(summary.total_km)} km`} />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-display text-sm font-semibold text-slate-900 mb-2">Fleet composition</h3>
              <div style={{ minHeight: 200 }} className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={summary.by_type} dataKey="value" nameKey="name" innerRadius={44} outerRadius={72} paddingAngle={2}>
                      {summary.by_type.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-1 space-y-1 text-xs">
                {summary.by_type.map((c, i) => (
                  <li key={c.name} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {c.name}
                    </span>
                    <span className="text-slate-800 font-medium tabular-nums">{c.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* View toggle */}
          <div className="mt-6 flex gap-2">
            <button data-testid="vehicles-tab-fleet" onClick={() => setView("fleet")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border ${view === "fleet" ? "bg-[#00334C] text-white border-[#00334C]" : "bg-white text-slate-600 border-slate-200"}`}>
              Fleet
            </button>
            <button data-testid="vehicles-tab-assets" onClick={() => setView("assets")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border ${view === "assets" ? "bg-[#00334C] text-white border-[#00334C]" : "bg-white text-slate-600 border-slate-200"}`}>
              Asset Management
            </button>
            <button data-testid="vehicles-tab-odometer" onClick={() => setView("odometer")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border ${view === "odometer" ? "bg-[#00334C] text-white border-[#00334C]" : "bg-white text-slate-600 border-slate-200"}`}>
              Odometer Coverage
            </button>
          </div>

          {view === "assets" ? (
            <AssetManagementTab />
          ) : view === "odometer" ? (
            <OdometerCoverageTab onSelectVehicle={setSelected} />
          ) : (
          <>
          {matchSummary && matchSummary.total_jobs > 0 && (
            <div data-testid="jobs-match-banner" className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
              <span>
                <b>{matchSummary.matched_jobs}</b> of <b>{matchSummary.total_jobs}</b> AroFlo service jobs matched to a vehicle across {matchSummary.vehicles_with_jobs} vehicles.
                {matchSummary.unmatched_jobs > 0 && <> {matchSummary.unmatched_jobs} couldn't be tied to a vehicle — AroFlo records no rego on the job, so these sit under a driver not assigned in the Hub.</>}
                {matchSummary.unmatched_jobs > 0 && (
                  <button data-testid="export-unmatched-btn" onClick={exportUnmatchedJobs}
                    className="ml-1 inline-flex items-center gap-1 font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900">
                    <Download className="w-3 h-3" /> Download the {matchSummary.unmatched_jobs}
                  </button>
                )}
              </span>
            </div>
          )}
          {/* Filters + table */}
          <div className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid={TID.vehicleSearch}
                  placeholder="Search rego, driver, model…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid={TID.vehicleStatusFilter} className="w-[170px] h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid={TID.vehicleTypeFilter} className="w-[150px] h-9">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {types.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={svcFilter} onValueChange={setSvcFilter}>
                <SelectTrigger data-testid="vehicle-service-filter" className="w-[160px] h-9">
                  <SelectValue placeholder="Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All service</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="due_soon">Due soon</SelectItem>
                  <SelectItem value="ok">OK</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <button data-testid="fleet-guide-btn" onClick={() => setShowGuide(true)}
                className="h-9 px-3 inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50">
                <BookOpen className="w-3.5 h-3.5" /> Guide
              </button>
              {can("vehicles", "edit") && (
                <button data-testid="fleet-settings-btn" onClick={() => setShowSettings(true)}
                  className="h-9 px-3 inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50">
                  <Settings className="w-3.5 h-3.5" /> Service alerts
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="fleet-export-menu"
                    className="h-9 px-3 inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50">
                    <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel className="text-xs">Reports (CSV)</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="export-asset-report" onClick={exportAssetReport}>Vehicle asset register</DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-service-due" onClick={exportServiceDue}>Service due list</DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-ampol-zero-km" onClick={exportAmpolZeroKm}>Ampol missing-km report</DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-replacement" onClick={exportReplacementShortlist}>Replacement shortlist</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="export-unmatched-menu" onClick={exportUnmatchedJobs}>Unmatched service jobs</DropdownMenuItem>
                  {can("vehicles", "edit") && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs">Email</DropdownMenuLabel>
                      <DropdownMenuItem data-testid="email-missing-km" onClick={emailMissingKmNudge}>Email missing-km nudge now</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {can("vehicles", "edit") && (
                <button data-testid="sync-job-costs-btn" onClick={syncJobCosts} disabled={costSyncing}
                  className="h-9 px-3 inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 ${costSyncing ? "animate-spin" : ""}`} /> {costSyncing ? "Syncing costs…" : "Sync job costs"}
                </button>
              )}
              <div className="ml-auto text-xs text-slate-500">{filtered.length} shown</div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    {[["Rego", "rego"], ["Vehicle", "make"], ["Type", "type"], ["Driver", "driver"], ["Base", "base"], ["Odometer", "odometer_km"], ["Last Service", "last_service_date"], ["Next Due", "days_to_due"], ["Status", "status"]].map(([c, k]) => (
                      <SortableTh key={k} label={c} k={k} sort={fleetSort} setSort={setFleetSort} className="text-left font-medium px-5 py-2.5" />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedFleet.map((v) => (
                    <tr
                      key={v.rego}
                      data-testid={TID.vehicleRow(v.rego)}
                      onClick={() => openVehicle(v.rego)}
                      className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-3 font-mono text-[13px] font-medium text-gbe">
                        <span className="inline-flex items-center gap-1.5">
                          {v.rego}
                          {v.data_origin && v.data_origin !== "synced" && (
                            <OriginBadge origin={v.data_origin} size="xs" showIcon={false} />
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-800">{v.make} {v.model}</div>
                        <div className="text-[11px] text-slate-400">{v.year} · {v.tags.slice(0, 2).join(" · ")}</div>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{v.type}</td>
                      <td className="px-5 py-3 text-slate-700">
                        {can("vehicles", "edit") && editRego === v.rego ? (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Input data-testid={`driver-input-${v.rego}`} value={editVal} autoFocus
                              onChange={(e) => setEditVal(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveDriver(v.rego); if (e.key === "Escape") setEditRego(null); }}
                              placeholder="Clear to reset" className="h-7 w-32 text-xs bg-white" />
                            <button data-testid={`driver-save-${v.rego}`} onClick={() => saveDriver(v.rego)} className="text-emerald-600 hover:text-emerald-700"><Check className="w-4 h-4" /></button>
                            <button data-testid={`driver-cancel-${v.rego}`} onClick={() => setEditRego(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 group">
                            <span>{v.driver || "—"}</span>
                            {can("vehicles", "edit") && (
                              <button data-testid={`driver-edit-${v.rego}`}
                                onClick={(e) => { e.stopPropagation(); setEditRego(v.rego); setEditVal(v.driver && v.driver !== "—" ? v.driver : ""); }}
                                title="Edit driver"
                                className="text-slate-400 hover:text-gbe hover:bg-slate-100 rounded p-0.5 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-600">{v.base}</td>
                      <td className="px-5 py-3 tabular-nums text-slate-700">{fmtNumber(v.odometer_km)} km</td>
                      <td className="px-5 py-3">
                        {v.last_service_date ? (
                          <div>
                            <div className={`tabular-nums text-[13px] ${v.service_overdue ? "text-rose-600 font-medium" : "text-slate-700"}`}>{v.last_service_date}</div>
                            {v.days_since_service != null && (
                              <div className={`text-[11px] ${v.service_overdue ? "text-rose-500" : "text-slate-400"}`}>
                                {v.days_since_service}d ago{v.service_overdue ? " · overdue" : ""}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        {v.next_due_date ? (
                          <div>
                            <div className={`tabular-nums text-[13px] ${v.service_status === "overdue" ? "text-rose-600 font-medium" : v.service_status === "due_soon" ? "text-amber-600 font-medium" : "text-slate-700"}`}>
                              {v.next_due_date}
                            </div>
                            <div className={`text-[11px] ${v.service_status === "overdue" ? "text-rose-500" : v.service_status === "due_soon" ? "text-amber-500" : "text-slate-400"}`}>
                              {v.days_to_due != null && (v.days_to_due < 0 ? `${-v.days_to_due}d overdue` : `in ${v.days_to_due}d`)}
                              {v.km_to_due != null && v.km_to_due < 0 ? " · km overdue" : ""}
                            </div>
                          </div>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[v.status]}`}>
                          {STATUS_LABEL[v.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-10 text-slate-500 text-sm">No vehicles match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>
          )}
        </>
      )}

      <ServiceSettingsDialog open={showSettings} onClose={() => setShowSettings(false)}
        onSaved={() => api.get("/vehicles/stats/summary").then((r) => setSummary(r.data))} />

      <FleetGuideSheet open={showGuide} onOpenChange={setShowGuide} />

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto">
          {selected && (
            <VehicleScorecard
              rego={selected}
              canEdit={can("vehicles", "edit")}
              onSaved={() => {
                api.get("/vehicles").then((r) => setVehicles(r.data));
                api.get("/vehicles/stats/summary").then((r) => setSummary(r.data));
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}

function Kpi({ label, value, icon: Icon, tone, onClick, selected, testId }) {
  const cls = {
    emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white",
    amber:   "border-amber-200 bg-gradient-to-br from-amber-50 to-white",
    blue:    "border-blue-200 bg-gradient-to-br from-blue-50 to-white",
    rose:    "border-rose-200 bg-gradient-to-br from-rose-50 to-white",
    slate:   "border-slate-200 bg-slate-50",
  }[tone] || "border-slate-200 bg-white";
  return (
    <div data-testid={testId} onClick={onClick}
      className={`rounded-lg p-4 border ${cls} ${onClick ? "cursor-pointer hover:shadow-sm transition-shadow" : ""} ${selected ? "ring-2 ring-[#00334C]" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}{onClick && selected ? " · filtered" : ""}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="mt-2 font-display text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

const SCORE_TONE = (s) => s >= 60 ? "text-rose-600" : s >= 40 ? "text-amber-600" : "text-emerald-700";
const AM_FIELDS = ["condition", "utilisation", "environment", "risk", "end_of_life"];

const ODO_STATE = {
  missing:  ["No reading", "bg-rose-50 text-rose-700 border-rose-200"],
  no_date:  ["No date", "bg-amber-50 text-amber-800 border-amber-200"],
  stale:    ["Stale", "bg-amber-50 text-amber-800 border-amber-200"],
  ok:       ["Current", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};

function OdometerCoverageTab({ onSelectVehicle }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("followup");

  useEffect(() => { api.get("/fleet/odometer-coverage?stale_days=90").then((r) => setData(r.data)); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.vehicles;
    if (filter === "missing") return data.vehicles.filter((v) => v.state === "missing" || v.state === "no_date");
    if (filter === "stale") return data.vehicles.filter((v) => v.state === "stale");
    return data.vehicles.filter((v) => v.state !== "ok"); // followup
  }, [data, filter]);

  const exportCsv = () => {
    const head = ["Rego", "Driver", "Base", "Vehicle", "Last odometer (km)", "As of", "Source", "Days since", "State"];
    const lines = rows.map((v) => [v.rego, v.driver || "", v.base || "", v.asset_name || `${v.make || ""} ${v.model || ""}`.trim(),
      v.odometer_km ?? "", v.odometer_as_of || "", v.odometer_source || "", v.days_since ?? "", ODO_STATE[v.state]?.[0] || v.state]
      .map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `odometer-followup-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!data) return <div className="mt-4 h-64 shimmer rounded-lg" />;

  return (
    <div className="mt-4" data-testid="odometer-coverage-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Odometer coverage" value={`${data.coverage_pct}%`} icon={Gauge} tone={data.coverage_pct >= 70 ? "emerald" : "amber"} testId="odo-kpi-coverage" />
        <Kpi label="Need follow-up" value={data.needs_follow_up} icon={AlertTriangle} tone={data.needs_follow_up ? "rose" : "emerald"} testId="odo-kpi-followup" />
        <Kpi label="No reading" value={data.counts.missing + data.counts.no_date} tone="rose" testId="odo-kpi-missing" />
        <Kpi label="Stale (>90d)" value={data.counts.stale} icon={Clock} tone="amber" testId="odo-kpi-stale" />
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 flex items-center justify-between gap-3 border-b border-slate-100">
          <div className="flex gap-2">
            {[["followup", "Needs follow-up"], ["missing", "No reading"], ["stale", "Stale"], ["all", "All active"]].map(([k, l]) => (
              <button key={k} data-testid={`odo-filter-${k}`} onClick={() => setFilter(k)}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${filter === k ? "bg-[#00334C] text-white border-[#00334C]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
            ))}
          </div>
          <button data-testid="odo-export-csv" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Vehicle</th>
                <th className="text-left px-4 py-2.5">Driver</th>
                <th className="text-left px-4 py-2.5">Base</th>
                <th className="text-right px-4 py-2.5">Last odometer</th>
                <th className="text-left px-4 py-2.5">As of / source</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((v) => {
                const [label, cls] = ODO_STATE[v.state] || ODO_STATE.missing;
                return (
                  <tr key={v.rego} data-testid={`odo-row-${v.rego}`} onClick={() => onSelectVehicle(v.rego)}
                    className="hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3 font-mono text-[13px] font-medium text-gbe">{v.rego}
                      <div className="font-sans text-[11px] text-slate-400">{v.asset_name || `${v.make || ""} ${v.model || ""}`.trim() || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{v.driver || <span className="text-slate-400">Unknown</span>}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{v.base || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{v.odometer_km ? `${fmtNumber(v.odometer_km)} km` : "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {v.odometer_as_of || "—"}{v.odometer_source ? ` · ${v.odometer_source}` : ""}
                      {v.days_since != null && <span className="text-slate-400"> ({v.days_since}d ago)</span>}
                    </td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}>{label}</span></td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">Nothing to follow up — all active vehicles have a current odometer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Odometer readings come from the monthly Ampol fuel import (drivers key their odometer at the pump). "No reading" and "Stale" vehicles need a driver to enter theirs at the next fill — or Jon can set it via the scorecard.
      </p>
    </div>
  );
}

function AssetManagementTab() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const { sorted: sortedAm, sort: amSort, setSort: setAmSort } = useSortable(data?.vehicles || []);

  const load = () => api.get("/vehicles/asset-management/ranking").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(AM_FIELDS.map((k) => [k, edit[k] ?? null]));
      body.eol_suggestion = edit.eol_suggestion ?? null;
      const res = await api.put(`/vehicles/${edit.rego}/asset-fields`, body);
      const af = res.data?.aroflo;
      if (af?.status === "ok") toast.success(`${edit.rego} saved · pushed to AroFlo`);
      else if (af?.status === "failed") toast.warning(`${edit.rego} saved in hub — AroFlo push failed (logged, will retry)`);
      else toast.success(`${edit.rego} saved${af?.reason ? ` · AroFlo: ${af.reason}` : ""}`);
      setEdit(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="mt-4 h-40 bg-white border border-slate-200 rounded-lg shimmer" />;
  return (
    <div className="mt-4" data-testid="asset-management-tab">
      <div className="text-xs text-slate-500 mb-3 max-w-3xl">{data.note} Score (0-100): condition 30 · risk 25 · odometer 20 · service age 15 · utilisation 10 — higher = stronger replacement candidate.</div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              {[["#", "rank"], ["Rego", "rego"], ["Vehicle", "asset_name"], ["Type", "type"], ["ODO", "odometer_km"], ["12-mo Spend", "maint_cost_12mo"], ["Condition", "condition"], ["Utilisation", "utilisation"], ["Environment", "environment"], ["Risk", "risk"], ["End of Life", "end_of_life"], ["Score", "replacement_score"]].map(([c, k]) => (
                <SortableTh key={k} label={c} k={k} sort={amSort} setSort={setAmSort} className="text-left font-medium px-3 py-2.5" />
              ))}
              <th className="text-left font-medium px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {sortedAm.map((v) => (
              <tr key={v.rego} data-testid={`am-row-${v.rego}`} className="border-b border-slate-100">
                <td className="px-3 py-2.5 text-slate-400 tabular-nums">{v.rank}</td>
                <td className="px-3 py-2.5 font-mono text-[13px] text-gbe">{v.rego}</td>
                <td className="px-3 py-2.5 text-slate-700">{v.asset_name || `${v.make || ""} ${v.model || ""}`.trim() || "—"}
                  {v.eol_suggestion && <div className="text-[11px] text-amber-600 italic max-w-[200px] truncate">"{v.eol_suggestion}"</div>}
                </td>
                <td className="px-3 py-2.5 text-slate-600">{v.type}</td>
                <td className="px-3 py-2.5 tabular-nums text-slate-600">{v.odometer_km ? `${fmtNumber(v.odometer_km)} km` : "—"}</td>
                <td className="px-3 py-2.5 tabular-nums text-slate-600">
                  {v.maint_cost_12mo ? `$${fmtNumber(v.maint_cost_12mo)}` : "—"}
                  {v.maint_trend_pct != null && v.maint_trend_pct !== 0 && (
                    <span className={`ml-1 text-[10px] ${v.maint_trend_pct > 0 ? "text-rose-500" : "text-emerald-600"}`}>{v.maint_trend_pct > 0 ? "+" : ""}{v.maint_trend_pct}%</span>
                  )}
                </td>
                {AM_FIELDS.map((k) => (
                  <td key={k} className="px-3 py-2.5 text-slate-600 text-[13px]">{v[k] ?? "—"}</td>
                ))}
                <td className={`px-3 py-2.5 font-semibold tabular-nums ${SCORE_TONE(v.replacement_score)}`}>
                  {v.replacement_score}
                  <span className="text-[10px] text-slate-400 font-normal"> /{v.signals_available} sig</span>
                </td>
                <td className="px-3 py-2.5">
                  <button data-testid={`am-edit-${v.rego}`} onClick={() => setEdit({ rego: v.rego, ...Object.fromEntries(AM_FIELDS.map((k) => [k, v[k] || ""])), eol_suggestion: v.eol_suggestion || "" })}
                    className="text-xs text-gbe underline underline-offset-2">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-lg p-5 w-[420px] max-w-[92vw]" onClick={(e) => e.stopPropagation()} data-testid="am-edit-dialog">
            <h3 className="font-display font-semibold text-slate-900 mb-3">Asset fields — {edit.rego}</h3>
            <div className="grid grid-cols-2 gap-3">
              {AM_FIELDS.map((k) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{k.replace(/_/g, " ")}</div>
                  <Input data-testid={`am-field-${k}`} value={edit[k]} placeholder={k === "end_of_life" ? "e.g. 2028" : "1-5 or Good/Poor"}
                    onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="h-8 text-sm" />
                </div>
              ))}
              <div className="col-span-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">End-of-life suggestion (manual note)</div>
                <Input data-testid="am-field-eol-suggestion" value={edit.eol_suggestion}
                  onChange={(e) => setEdit({ ...edit, eol_suggestion: e.target.value })} className="h-8 text-sm"
                  placeholder="e.g. Replace FY28 — gearbox issues, high kms" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-3 py-1.5 text-xs border border-slate-200 rounded-md">Cancel</button>
              <button data-testid="am-save-btn" disabled={saving} onClick={save}
                className="px-3 py-1.5 text-xs bg-[#00334C] text-white rounded-md">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
